import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import * as z from 'zod';
import { ApiError } from '@/shared/api/ApiError';
import { env } from '@/shared/config/env';
import loginHero from '@/assets/landing/login-hero.jpg';
import logoSign from '@/assets/landing/logo-sign.svg';
import logoText from '@/assets/landing/logo-text.svg';
import socialFacebook from '@/assets/landing/social-facebook.svg';
import socialInstagram from '@/assets/landing/social-instagram.svg';
import socialTiktok from '@/assets/landing/social-tiktok.svg';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  FormField,
  Input,
  PasswordInput,
} from '@/shared/ui';
import { useAuth } from '../model/auth-context';
import { AppleIcon, GoogleIcon } from './SocialIcons';

/** Buduje schemat walidacji z komunikatami w jezyku uzytkownika. */
function createLoginSchema(messages: {
  emailRequired: string;
  emailInvalid: string;
  passwordRequired: string;
}) {
  return z.object({
    email: z
      .string()
      .trim()
      .min(1, { message: messages.emailRequired })
      .pipe(z.email({ message: messages.emailInvalid })),
    password: z.string().min(1, { message: messages.passwordRequired }),
    rememberMe: z.boolean(),
  });
}

type LoginFormValues = z.infer<ReturnType<typeof createLoginSchema>>;

/** Ekran logowania — spojny z landingiem tenify.pl (tlo banera, logo, media). */
export function LoginPage() {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [formError, setFormError] = useState<string | null>(null);

  const schema = useMemo(
    () =>
      createLoginSchema({
        emailRequired: t('validation.emailRequired'),
        emailInvalid: t('validation.emailInvalid'),
        passwordRequired: t('validation.passwordRequired'),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', rememberMe: true },
  });

  // Zalogowany uzytkownik nie ma po co ogladac ekranu logowania.
  if (status === 'authenticated') {
    return <Navigate to="/turnieje" replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await login(values.email, values.password, values.rememberMe);

      // Po zalogowaniu ladujemy prosto w module turniejow — chyba ze uzytkownik
      // przyszedl z konkretnego adresu chronionego, wtedy wracamy do niego.
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      await navigate(from ?? '/turnieje', { replace: true });
    } catch (error) {
      setFormError(t(resolveLoginErrorKey(error)));
    }
  });

  return (
    <div className="bg-landing-black relative min-h-dvh">
      {/* Tlo 1:1 z banerem landingu — zdjecie i przyciemnienie jak w hero. */}
      <img src={loginHero} alt="" className="absolute inset-0 size-full object-cover" />
      <div className="absolute inset-0 bg-black/20" aria-hidden />

      <div className="relative mx-auto flex min-h-dvh max-w-[1600px] flex-col px-5 py-6 lg:px-10 lg:py-8">
        {/* Pasek gorny jak menu landingu: oryginalne logo + odnosniki do mediow. */}
        <header className="flex items-center justify-between">
          <span className="flex items-center gap-[15px]">
            <img src={logoSign} alt="" draggable={false} className="h-14 lg:h-[4.875rem]" />
            <img src={logoText} alt="Tenify" draggable={false} className="h-9 lg:h-[3.25rem]" />
          </span>
          <nav className="flex items-center gap-2.5" aria-label="Media społecznościowe">
            <a href="#" aria-label="Tenify na Instagramie">
              <img src={socialInstagram} alt="" className="size-10" />
            </a>
            <a href="#" aria-label="Tenify na Facebooku">
              <img src={socialFacebook} alt="" className="size-10" />
            </a>
            <a href="#" aria-label="Tenify na TikToku">
              <img src={socialTiktok} alt="" className="size-10" />
            </a>
          </nav>
        </header>

        {/* Box logowania wysrodkowany na tle banera. */}
        <main className="flex flex-1 items-center justify-center py-8">
          <Card
              elevation="floating"
              padding="none"
              className="w-full max-w-[460px] rounded-3xl p-7 sm:p-9"
            >
              <div className="text-center">
                {/* Napis z oryginalnego logo zamiast tekstu — spojnosc z landingiem. */}
                <img src={logoText} alt={t('login.title')} className="mx-auto h-9 w-auto" />
                <p className="text-fg-muted mt-3 text-sm">{t('login.subtitle')}</p>
              </div>

              <div className="mt-7 flex flex-col gap-3">
                {/* Logowanie zewnetrzne nie jest jeszcze obsluzone przez backend
                    (brak endpointow OAuth), wiec przyciski sa wylaczone. */}
                <Button
                  variant="secondary"
                  size="lg"
                  fullWidth
                  disabled
                  title="Logowanie przez Google będzie dostępne wkrótce"
                  leadingIcon={<GoogleIcon className="size-5" />}
                >
                  {t('login.google')}
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  fullWidth
                  disabled
                  title="Logowanie przez Apple będzie dostępne wkrótce"
                  leadingIcon={<AppleIcon className="size-5" />}
                >
                  {t('login.apple')}
                </Button>
              </div>

              <Divider label={t('login.or')} className="my-6" />

              <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
                {formError && (
                  <Alert tone="danger" className="py-3">
                    {formError}
                  </Alert>
                )}

                <FormField label={t('login.emailLabel')} error={errors.email?.message}>
                  <Input
                    type="email"
                    inputSize="lg"
                    autoComplete="email"
                    autoFocus
                    placeholder={t('login.emailPlaceholder')}
                    {...register('email')}
                  />
                </FormField>

                <FormField label={t('login.passwordLabel')} error={errors.password?.message}>
                  <PasswordInput
                    inputSize="lg"
                    placeholder={t('login.passwordPlaceholder')}
                    {...register('password')}
                  />
                </FormField>

                <div className="flex items-center justify-between gap-3">
                  <Checkbox label={t('login.rememberMe')} {...register('rememberMe')} />
                  <Link
                    to="/odzyskiwanie-hasla"
                    className="text-brand-text text-[0.8125rem] font-semibold hover:underline"
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </div>

                <Button type="submit" size="lg" fullWidth loading={isSubmitting} className="mt-2">
                  {isSubmitting ? t('login.submitting') : t('login.submit')}
                </Button>
              </form>

              <p className="text-fg-muted mt-6 text-center text-[0.8125rem]">
                {t('login.noAccount')}{' '}
                <Link to="/rejestracja" className="text-brand-text font-semibold hover:underline">
                  {t('login.signUp')}
                </Link>
              </p>
          </Card>
        </main>

        <footer className="flex flex-col items-center justify-between gap-3 text-xs text-white/70 sm:flex-row">
          <p>{tCommon('legal.copyright', { year: new Date().getFullYear() })}</p>
          <nav className="flex items-center gap-6">
            <a
              href={`${env.landingUrl}/polityka-prywatnosci`}
              className="hover:text-landing-yellow transition-colors"
            >
              {tCommon('legal.privacy')}
            </a>
            <a
              href={`${env.landingUrl}/regulamin`}
              className="hover:text-landing-yellow transition-colors"
            >
              {tCommon('legal.terms')}
            </a>
            <a
              href={`${env.landingUrl}/kontakt`}
              className="hover:text-landing-yellow transition-colors"
            >
              {tCommon('legal.contact')}
            </a>
          </nav>
        </footer>
      </div>
    </div>
  );
}

/**
 * Mapuje blad z API na klucz komunikatu dla uzytkownika.
 *
 * UWAGA — niespojnosc backendu: `user-service` nie ma `GlobalExceptionHandler`,
 * wiec bledne haslo konczy sie odpowiedzia 500 zamiast 401, w dodatku bez tresci
 * komunikatu (`server.error.include-message` jest domyslnie `never`).
 * Obslugujemy oba warianty: poprawny 401 oraz 500 z trescia bledu, gdyby
 * kiedykolwiek zostala wlaczona.
 */
function resolveLoginErrorKey(error: unknown): 'errors.invalidCredentials' | 'errors.serverError' {
  if (!(error instanceof ApiError) || error.isNetworkError) {
    return 'errors.serverError';
  }

  if (error.status === 401 || error.status === 400) {
    return 'errors.invalidCredentials';
  }

  if (error.detail?.toLowerCase().includes('invalid email or password')) {
    return 'errors.invalidCredentials';
  }

  return 'errors.serverError';
}
